import React, { useRef, useState, useCallback } from 'react';
import { X, Phone, Mail, Clock, Calendar, Zap, FileText } from 'lucide-react';
import type { Client } from '../types';
import { getLeadRecommendation } from '../utils/leadRecommendation';

interface FloatingClientCardProps {
  client: Client;
  onClose: () => void;
  onGenerateMessage?: (client: Client) => void;
}

const activityTypeLabels: Record<string, string> = {
  call: 'שיחה',
  whatsapp: 'WhatsApp',
  meeting: 'פגישה',
  property_sent: 'שליחת נכס',
  note: 'הערה',
};

const customerStatusLabels: Record<string, { label: string; color: string }> = {
  new: { label: 'חדש', color: 'bg-sky-600' },
  in_progress: { label: 'בטיפול', color: 'bg-yellow-600' },
  available: { label: 'זמין', color: 'bg-green-600' },
  unavailable: { label: 'לא זמין', color: 'bg-gray-500' },
  serious: { label: 'רציני', color: 'bg-emerald-600' },
  met: { label: 'ניפגש', color: 'bg-cyan-600' },
  property_visit: { label: 'ביקור בנכס', color: 'bg-teal-600' },
  deal_closed: { label: 'נסגרה עסקה', color: 'bg-green-700' },
  closed: { label: 'נסגרה עסקה', color: 'bg-green-700' },
  not_relevant: { label: 'לא רלוונטי', color: 'bg-red-600' },
};

function formatDate(utcString: string | null | undefined): string {
  if (!utcString) return '—';
  const d = new Date(utcString);
  const pad = (n: number) => n.toString().padStart(2, '0');
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  if (isToday) return `היום ${time}`;
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)} ${time}`;
}

export default function FloatingClientCard({ client, onClose, onGenerateMessage }: FloatingClientCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ x: window.innerWidth / 2 - 340, y: 80 });
  const dragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button, a, input, textarea')) return;
    dragging.current = true;
    dragOffset.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [pos]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!dragging.current) return;
    setPos({ x: e.clientX - dragOffset.current.x, y: e.clientY - dragOffset.current.y });
  }, []);

  const handleMouseUp = useCallback(() => {
    dragging.current = false;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseMove]);

  const status = client.customerStatus || 'new';
  const statusInfo = customerStatusLabels[status];
  const lastActivity = client.lastActivity;

  return (
    <div
      ref={cardRef}
      dir="rtl"
      className="fixed z-[55] bg-gray-800 border border-gray-600 rounded-xl shadow-2xl select-none"
      style={{
        left: pos.x,
        top: pos.y,
        width: 'min(680px, 80vw)',
        cursor: dragging.current ? 'grabbing' : 'grab',
      }}
      onMouseDown={handleMouseDown}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700 bg-gray-750 rounded-t-xl">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-lg font-bold text-white">{client.name}</h2>
          {statusInfo && (
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium text-white ${statusInfo.color}`}>
              {statusInfo.label}
            </span>
          )}
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
            client.leadType === 'buyer' ? 'bg-blue-600 text-white' : 'bg-orange-600 text-white'
          }`}>
            {client.leadType === 'buyer' ? 'קונה' : 'מוכר'}
          </span>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-white transition-colors p-1 rounded hover:bg-gray-700"
        >
          <X size={20} />
        </button>
      </div>

      {/* Body */}
      <div className="p-5 space-y-4 max-h-[60vh] overflow-y-auto cursor-default" onMouseDown={e => e.stopPropagation()}>
        {/* Contact Info */}
        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-center gap-2 text-sm">
            <Phone size={14} className="text-gray-500 shrink-0" />
            <span className="text-gray-300">{client.phone}</span>
          </div>
          {client.email && (
            <div className="flex items-center gap-2 text-sm">
              <Mail size={14} className="text-gray-500 shrink-0" />
              <span className="text-gray-300 truncate">{client.email}</span>
            </div>
          )}
        </div>

        {/* Dates & Actions */}
        <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-700">
          <div className="flex items-center gap-2 text-sm">
            <Clock size={14} className="text-gray-500 shrink-0" />
            <div>
              <span className="text-gray-500">קשר אחרון: </span>
              <span className="text-gray-300">{formatDate(client.lastContactAt)}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Calendar size={14} className="text-gray-500 shrink-0" />
            <div>
              <span className="text-gray-500">מעקב הבא: </span>
              <span className="text-gray-300">{formatDate(client.nextFollowUpAt)}</span>
            </div>
          </div>
          {client.nextAction && (
            <div className="flex items-center gap-2 text-sm col-span-2">
              <Zap size={14} className="text-sky-400 shrink-0" />
              <div>
                <span className="text-gray-500">פעולה הבאה: </span>
                <span className="text-gray-300">{client.nextAction}</span>
              </div>
            </div>
          )}
          {lastActivity && (
            <div className="flex items-center gap-2 text-sm col-span-2">
              <FileText size={14} className="text-cyan-400 shrink-0" />
              <div>
                <span className="text-gray-500">פעילות אחרונה: </span>
                <span className="text-gray-300">
                  {activityTypeLabels[lastActivity.activityType] || lastActivity.activityType} – {formatDate(lastActivity.createdAt)}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Notes */}
        {client.notes && (
          <div className="pt-3 border-t border-gray-700">
            <p className="text-xs text-gray-500 mb-1">הערות:</p>
            <p className="text-sm text-gray-300 whitespace-pre-wrap">{client.notes}</p>
          </div>
        )}

        {/* AI Recommendation */}
        {(() => {
          const rec = getLeadRecommendation(client);
          if (!rec) return null;
          return (
            <div className="pt-3 border-t border-gray-700">
              <div className="rounded-lg bg-gray-750 border border-gray-600 p-3">
                <div className="flex items-start gap-2">
                  <span className="text-sm mt-0.5">🤖</span>
                  <div className="flex-1">
                    <p className="text-sm text-sky-300 font-medium mb-1">{rec.short}</p>
                    <p className="text-xs text-gray-400">{rec.detail}</p>
                  </div>
                  <button
                    onClick={() => onGenerateMessage?.(client)}
                    className="shrink-0 text-xs px-2.5 py-1 rounded bg-gray-600 text-gray-300 hover:bg-gray-500 transition-colors"
                    title="צור הודעה"
                  >
                    צור הודעה
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Future area placeholder */}
        <div className="pt-3 border-t border-gray-700/50">
          <div className="rounded-lg border border-dashed border-gray-700 p-4 text-center">
            <p className="text-xs text-gray-600">מקום שמור לפעילות עתידית ו-AI</p>
          </div>
        </div>
      </div>
    </div>
  );
}
